import { useState } from 'react';
import { Stack, Text } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IContainerProps } from '~/components/utils/Container';
import { SearchArticlesFormBody } from '~/components/forms/SearchArticlesForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import { SearchArticlesListItem } from '~/components/molecules/SearchArticlesListItem';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { useSearchArticlesQuery } from '~/lib/api/modules/Articles/hooks/useSearchArticlesQuery';

const GRID_TEMPLATE_COLUMNS = '1fr repeat(3, min-content)';

export type ISearchArticlesProps = IContainerProps & {
  appId: string;
};

const styles = stylex.create({
  sectionTitle: {
    marginLeft: '1rem',
  },
});

export const SearchArticles: React.FC<ISearchArticlesProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [text, setText] = useState(searchParams.get('text') ?? '');
  const pagination = usePagination({ extraParams: { text } });
  const searchArticlesQuery = useSearchArticlesQuery(
    {
      appId,
      body: {
        text,
        skip: pagination.start,
        limit: pagination.limit,
        published: undefined,
        trashed: undefined,
      },
    },
    {
      enabled: !!text && pagination.limit > 0,
      keepPreviousData: true,
    },
  );

  return (
    <ContentBlock sx={sx}>
      <SearchArticlesFormBody
        first
        onSubmit={(values) => {
          setText(values.text ?? '');
          pagination.updatePage(0);
        }}
        loading={searchArticlesQuery.isFetching}
        defaultValues={{ text }}
      />

      {searchArticlesQuery.isError ? (
        <ErrorMessage error={searchArticlesQuery.error} />
      ) : !searchArticlesQuery.data?.items?.length ? (
        !searchArticlesQuery.isLoading && !!text ? (
          <ContentBlock.Empty
            title={t('SearchArticles.noResult.title')}
            subtitle={t('SearchArticles.noResult.subtitle')}
          />
        ) : (
          <ContentBlock.Empty
            title={t('SearchArticles.empty.title')}
            subtitle={t('SearchArticles.empty.subtitle')}
          />
        )
      ) : (
        <>
          <Stack>
            <Text sx={styles.sectionTitle} variant="label" size="lg">
              {t('SearchArticles.title', {
                count: searchArticlesQuery.data.total,
              })}
            </Text>
            <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
              {searchArticlesQuery.data.items.map((article, articleIndex) => (
                <SearchArticlesListItem
                  key={article._id}
                  first={articleIndex === 0}
                  article={article}
                />
              ))}
            </Table>
          </Stack>

          <ContentBlock.Footer>
            <Pagination
              pagination={pagination}
              total={searchArticlesQuery.data?.total}
            />
          </ContentBlock.Footer>
        </>
      )}
    </ContentBlock>
  );
};
